import type { GQL_ID, CommonSort } from '@@partner:types:graphql'
import type { Feedback } from '@@partner:types:app'
import type { SortType } from 'rsuite-table'

import React, { useCallback, useState } from 'react'

import AppLoader from '@components/common/AppLoader'
import { Table, Button, Avatar, Rate } from 'rsuite'
import { generatePath, Link } from 'react-router-dom'

import { SORT_ORDER_DESC } from '@@partner:config/graphql'
import { NO_PHOTO_IMAGE } from '@@partner:config/image'
import { ROUTE_ORDERS_VIEW_ORDER } from '@@partner:config/routes'
import { usePartner } from '@@partner:helpers/hooks/auth'

import FeedbackModal from '@components/feedback/modal/FeedbackModal'
import { useAddFeedbackComment } from '@@partner:graphql/mutation/feedback/addFeedbackComment.gql'

import './FeedbacksTable.scss'

export interface FeedbacksTableProps {
  tableSort: CommonSort
  loading: boolean
  feedbacks: Feedback[]
  generateOpenUrl?: (feedbackId: GQL_ID) => string
  onSortColumn: (column: string, sortType?: SortType) => void
}

const { Column, HeaderCell, Cell } = Table

export default function FeedbacksTable ({
  tableSort,
  loading = false,
  feedbacks = [],
  generateOpenUrl,
  onSortColumn
}: FeedbacksTableProps) {
  const { brandSlug } = usePartner()
  const [ selectedFeedback, setSelectedFeedback ] = useState<Feedback | null>(null)
  const [ isModalOpen, setIsModalOpen ] = useState(false)
  
  const [ addFeedbackComment ] = useAddFeedbackComment()

  const handleOpenOrder = useCallback((brandSlug: string, orderId?: GQL_ID) => {
    if (!orderId) return ''
    return generatePath(ROUTE_ORDERS_VIEW_ORDER, {
      brandSlug,
      orderId: orderId.toString()
    })
  }, [])

  const handleViewFeedback = useCallback((feedback: Feedback) => {
    setSelectedFeedback(feedback)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedFeedback(null)
  }, [])

  const handleCommentSubmit = useCallback(async (feedbackId: GQL_ID, comment: string) => {
    await addFeedbackComment({
      variables: {
        feedback_id: feedbackId,
        comment
      }
    })
  }, [ addFeedbackComment ])

  return (
    <>
      <Table
        data={feedbacks}
        autoHeight
        wordWrap
        sortColumn={tableSort.fieldName}
        sortType={tableSort.direction === SORT_ORDER_DESC ? 'desc' : 'asc'}
        onSortColumn={onSortColumn}
        loading={loading}
        renderLoading={() =>
          loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
        }
        affixHeader
        rowHeight={100}
        className="FeedbacksTable"
      >
        <Column flexGrow={1} verticalAlign="middle" sortable>
          <HeaderCell>ИД</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={2} verticalAlign="middle">
          <HeaderCell>Изображения</HeaderCell>
          <Cell dataKey="images">
            {(feedback: Feedback) =>
              feedback.images.map((image) => (
                <Avatar src={image.url ?? NO_PHOTO_IMAGE} size="lg" />
              ))
            }
          </Cell>
        </Column>
        <Column flexGrow={2} verticalAlign="middle">
          <HeaderCell>Пользователь</HeaderCell>
          <Cell dataKey="customer_name">
            {(feedback: Feedback) =>
              feedback.customer.first_name + ' ' + feedback.customer.last_name
            }
          </Cell>
        </Column>
        <Column flexGrow={2} verticalAlign="middle" sortable>
          <HeaderCell>Магазин</HeaderCell>
          <Cell dataKey="shop.address">
            {(feedback: Feedback) => (
              <>
                { feedback?.shop?.name ? feedback?.shop?.name + ', ' : '' }
                {feedback?.shop?.address}
              </>
            )}
          </Cell>
        </Column>

        <Column flexGrow={3} verticalAlign="middle">
          <HeaderCell>Отзыв</HeaderCell>
          <Cell dataKey="review">
            {(feedback: Feedback) => (
              <span style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                fontSize: '12px'
              }}>
                {feedback.review || '—'}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} verticalAlign="middle" sortable>
          <HeaderCell>Рейтинг</HeaderCell>
          <Cell dataKey="general_rating">
            {(feedback: Feedback) => (
              <Rate
                value={feedback.general_rating}
                readOnly
                className="FeedbacksTable__Rate"
                size="xs"
              />
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} verticalAlign="middle">
          <HeaderCell>Звонок покупателю</HeaderCell>
          <Cell dataKey="need_call">
            {(feedback: Feedback) =>
              feedback.need_call ? 'Требуется' : 'Не требуется'
            }
          </Cell>
        </Column>

        <Column flexGrow={1} fixed="right" verticalAlign="middle">
          <HeaderCell>Управление</HeaderCell>
          <Cell>
            {(feedback: Feedback) => (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                <Button
                  color="cyan"
                  onClick={() => handleViewFeedback(feedback)}
                  size="xs"
                >
                  Посмотреть
                </Button>
                {feedback.order_id && (
                  <Button
                    color="blue"
                    as={Link}
                    to={handleOpenOrder(brandSlug, feedback.order_id)}
                    size="xs"
                  >
                    Заказ
                  </Button>
                )}
              </div>
            )}
          </Cell>
        </Column>
      </Table>
      
      <FeedbackModal
        feedback={selectedFeedback}
        open={isModalOpen}
        onClose={handleCloseModal}
        onCommentSubmit={handleCommentSubmit}
      />
    </>
  )
}
